import { FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation, FAIcon, Theme, Shade, IconStyle, Tag, TagStyle } from '@nextgen-web-framework/all';
import type { AppState } from '@nextgen-web-framework/core';
import { downlineAddressType } from '@commerce-profile-webcomponents/sdk';
import AddressCard from '../AddressList/address-card/AddressCard';
import type { DownlineAddressProps } from './types/propTypes';
import styles from './DownlineAddressList.module.scss';

const DownlineAddressList: FC<DownlineAddressProps> = ({
  downlineAddressList,
  isSelectedAddress,
  handleDownlinerClick,
  setSelectedDownlinerDetails,
}) => {
  const selectedAddressRef = useRef<HTMLDivElement>(null);

  const downliner = useSelector((state: AppState) => state.commerceProfile.downlineAddressDetails);
  const { downLinerOwnerId } = downliner;
  const t = useTranslation();
  useEffect(() => {
    if (selectedAddressRef.current && selectedAddressRef.current.scrollIntoView) {
      selectedAddressRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedAddressRef]);

  return (
    <>
      {downlineAddressList && (
        <div className={styles['downline-address-container']}>
          <div className={styles['downline-address-heading']}>{t('profile.downlinAddress.heading')}</div>
          {downlineAddressList.map((downliner: downlineAddressType) => {
            const { firstName, lastName, accountId, ownerId, coApplicantIds, coApplicantFname, coApplicantLname } =
              downliner;

            const handleAddressClick = () => {
              const downlineobj: downlineAddressType = {
                firstName,
                lastName,
                accountId,
                ownerId,
                coApplicantIds,
                coApplicantFname: coApplicantFname || firstName,
                coApplicantLname: coApplicantLname || lastName,
              };
              if (isSelectedAddress?.ownerId && coApplicantIds?.includes(isSelectedAddress?.ownerId)) {
                setSelectedDownlinerDetails(downlineobj, 1);
                handleDownlinerClick(
                  coApplicantIds[0],
                  coApplicantFname || firstName,
                  coApplicantLname || lastName,
                  accountId,
                  coApplicantIds,
                  coApplicantFname || firstName,
                  coApplicantLname || lastName,
                );
              } else {
                setSelectedDownlinerDetails(downlineobj, 0);
                handleDownlinerClick(
                  ownerId,
                  firstName,
                  lastName,
                  accountId,
                  coApplicantIds,
                  coApplicantFname || firstName,
                  coApplicantLname || lastName,
                  false,
                );
              }
            };

            return (
              <div className={styles['user-address-container']} key={ownerId}>
                <div
                  className={styles['user-address-display']}
                  data-testid="downline-address-display"
                  role="presentation"
                  onClick={handleAddressClick}
                  onKeyDown={handleAddressClick}
                >
                  <div className={styles['user-address-details']}>
                    {firstName && <span>{firstName} </span>} &nbsp;
                    {lastName && <span>{lastName}</span>}
                    {accountId && (
                      <div className={styles['user-address-account-no']}>
                        {t('profile.downlinAddress.accountNo')} {accountId}
                      </div>
                    )}
                  </div>

                  {downLinerOwnerId === ownerId && (
                    <div className={styles['user-address-tag']}>
                      <Tag text={t('profile.addressBook.noAddress')} style={TagStyle.OUTLINE} theme={Theme.GRAY} />
                    </div>
                  )}

                  <div className={styles['user-address-icon']}>
                    <FAIcon
                      theme={Theme.BLACK}
                      data-testid="test-faicon-id"
                      iconName={'chevron-right'}
                      shade={Shade.DARK}
                      iconStyle={IconStyle.SOLID}
                    />
                  </div>
                </div>
                {isSelectedAddress?.ownerId &&
                  (isSelectedAddress?.ownerId === ownerId || coApplicantIds?.includes(isSelectedAddress?.ownerId)) && (
                    <div className={styles['downline-address-text-divider']} ref={selectedAddressRef}>
                      <AddressCard
                        selectedAddress={true}
                        userAddress={isSelectedAddress}
                        isDownlineAddressOpen={true}
                        allowSelectInvalidAddress
                      />
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
export default DownlineAddressList;
